import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  WORKFLOW_CATALOGUE,
  type WorkflowEdge,
  type WorkflowGraph,
  type WorkflowNode,
  type WorkflowSubject,
  type WorkflowTrigger,
} from "@dentacore/shared-types";

import {
  ADD_GAP,
  ADD_H,
  ADD_W,
  BOX_W,
  GAP,
  GUESS_H,
  ROW_GAP,
  conditionSummary,
  nodeAction,
  openEnds,
  stepNumbers,
  swapPartner,
  targetOptions,
  targetSubject,
  triggerSummary,
  waitSummary,
  type NewBoxKind,
  type OpenEnd,
} from "./graph-model";
import type { WorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

/**
 * **The canvas** (WF-D7–WF-D9, WG-D4). Boxes sit where they were put and drag
 * freely; the arrows and their condition chips follow a dragged box. Nothing
 * here lines anything up — *Tidy up* and *Save* do that, in the editor. The
 * empty canvas drags sideways like the scrollbar does (WF-D9).
 *
 * The board measures its boxes after each render so the arrows meet them in
 * the middle whatever their content, and tells the editor the heights so a new
 * box and *Tidy up* can sit on the same line.
 */

export type Selection = { kind: "node"; id: string } | { kind: "edge"; id: string } | null;

export interface BoardHandle {
  viewportHeight: () => number;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A box's icon, one `d` on a 24-unit box at stroke 1.7 — the sidebar's idiom. */
const ICONS = {
  start: "M8 5v14l11-7z",
  auto: "M13 3 5 14h6l-1 7 8-11h-6z",
  person: "M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6",
  fork: "M6 4v6a4 4 0 0 0 4 4h8M6 10v10M15 11l3 3-3 3",
  wait: "M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17M12 7.5V12l3 2",
  plus: "M12 5v14M5 12h14",
} as const;

function Icon({ d }: { d: string }): React.JSX.Element {
  return (
    <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function Board({
  graph,
  subject,
  trigger,
  selection,
  refs,
  hasTasks,
  handle,
  scrollTarget,
  onSelect,
  onMove,
  onAdd,
  onSwap,
  onRemove,
  onAction,
  onMeasure,
}: {
  graph: WorkflowGraph;
  subject: WorkflowSubject;
  trigger: WorkflowTrigger;
  selection: Selection;
  refs: WorkflowRefs;
  hasTasks: boolean;
  handle: React.RefObject<BoardHandle | null>;
  /** A box to bring into view — a new one, after *Add*. The nonce makes a repeat count. */
  scrollTarget: { id: string; nonce: number } | null;
  onSelect: (selection: Selection) => void;
  onMove: (id: string, x: number, y: number) => void;
  onAdd: (end: OpenEnd, kind: NewBoxKind) => void;
  onSwap: (id: string, direction: "left" | "right") => void;
  onRemove: (id: string) => void;
  onAction: (id: string, action: string) => void;
  onMeasure: (heights: ReadonlyMap<string, number>) => void;
}): React.JSX.Element {
  const view = useRef<HTMLDivElement | null>(null);
  const elements = useRef(new Map<string, HTMLElement>());
  const [sizes, setSizes] = useState<ReadonlyMap<string, { w: number; h: number }>>(new Map());
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);
  const dragStart = useRef<{
    id: string;
    sx: number;
    sy: number;
    ox: number;
    oy: number;
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const ring = trigger.kind === "ring";

  handle.current = { viewportHeight: () => view.current?.clientHeight ?? 480 };

  // Measure every box after each render; a new size redraws the arrows once.
  useLayoutEffect(() => {
    let changed = false;
    const next = new Map<string, { w: number; h: number }>();
    for (const [id, element] of elements.current) {
      const size = { w: element.offsetWidth, h: element.offsetHeight };
      next.set(id, size);
      const before = sizes.get(id);
      if (before === undefined || before.w !== size.w || before.h !== size.h) changed = true;
    }
    if (next.size !== sizes.size) changed = true;
    if (changed) {
      setSizes(next);
      onMeasure(new Map([...next].map(([id, s]) => [id, s.h])));
    }
  });

  // Bring a new box into view (WF-D8).
  useEffect(() => {
    if (scrollTarget === null) return;
    const node = graph.nodes.find((n) => n.id === scrollTarget.id);
    const box = view.current;
    if (node === undefined || box === null) return;
    const right = node.x + BOX_W + ADD_GAP + ADD_W + 24;
    if (right > box.scrollLeft + box.clientWidth)
      box.scrollTo({ left: Math.max(0, right - box.clientWidth), behavior: "smooth" });
    else if (node.x < box.scrollLeft)
      box.scrollTo({ left: Math.max(0, node.x - 24), behavior: "smooth" });
    const bottom = node.y + (sizes.get(node.id)?.h ?? GUESS_H) + 24;
    if (bottom > box.scrollTop + box.clientHeight)
      box.scrollTo({ top: bottom - box.clientHeight, behavior: "smooth" });
    // Only a new target scrolls; sizes settling afterwards must not drag the view back.
  }, [scrollTarget?.nonce]);

  const rectOf = (node: WorkflowNode): Rect => {
    const at = drag?.id === node.id ? drag : node;
    const size = sizes.get(node.id);
    return { x: at.x, y: at.y, w: size?.w ?? BOX_W, h: size?.h ?? GUESS_H };
  };
  const rects = new Map(graph.nodes.map((n) => [n.id, rectOf(n)]));

  // The dashed *Add* boxes, one on every free way out.
  const adds = openEnds(graph).map((end) => {
    const from = rects.get(end.from) as Rect;
    const fork = graph.nodes.find((n) => n.id === end.from)?.kind === "fork";
    const x = from.x + from.w + (fork ? GAP : ADD_GAP);
    const y = end.branch === "no" ? from.y + from.h + ROW_GAP : from.y + from.h / 2 - ADD_H / 2;
    return { end, rect: { x, y, w: ADD_W, h: ADD_H } };
  });

  const all = [...rects.values(), ...adds.map((a) => a.rect)];
  const width = Math.max(...all.map((r) => r.x + r.w), 0) + 200;
  const height = Math.max(...all.map((r) => r.y + r.h), 0) + 80;

  const numbers = stepNumbers(graph);

  // ── Dragging a box, and the empty canvas ─────────────────────────────────

  const startPan = (event: React.PointerEvent<HTMLDivElement>): void => {
    const target = event.target as Element;
    if (event.button !== 0 || target.closest("[data-node-id], [data-chip], [data-add]") !== null)
      return;
    const box = view.current;
    if (box === null) return;
    const sx = event.clientX;
    const sy = event.clientY;
    const left = box.scrollLeft;
    const top = box.scrollTop;
    const board = event.currentTarget;
    board.classList.add(styles.panning ?? "");
    const move = (m: PointerEvent): void => {
      box.scrollLeft = left - (m.clientX - sx);
      box.scrollTop = top - (m.clientY - sy);
    };
    const stop = (): void => {
      board.classList.remove(styles.panning ?? "");
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  const startDrag = (event: React.PointerEvent<HTMLDivElement>, node: WorkflowNode): void => {
    if (event.button !== 0) return;
    if ((event.target as Element).closest("select, input, button, textarea, label") !== null)
      return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = {
      id: node.id,
      sx: event.clientX,
      sy: event.clientY,
      ox: node.x,
      oy: node.y,
      x: node.x,
      y: node.y,
      moved: false,
    };
  };

  const moveDrag = (event: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragStart.current;
    if (d === null) return;
    const x = Math.max(0, Math.round(d.ox + event.clientX - d.sx));
    const y = Math.max(0, Math.round(d.oy + event.clientY - d.sy));
    if (!d.moved && Math.abs(x - d.ox) + Math.abs(y - d.oy) < 4) return;
    d.moved = true;
    d.x = x;
    d.y = y;
    setDrag({ id: d.id, x, y });
  };

  const endDrag = (): void => {
    const d = dragStart.current;
    dragStart.current = null;
    if (d === null) return;
    // The ref holds the last position: a move not yet drawn still counts.
    if (d.moved) onMove(d.id, d.x, d.y);
    else onSelect({ kind: "node", id: d.id });
    setDrag(null);
  };

  // ── Arrows ───────────────────────────────────────────────────────────────

  const wire = (a: Rect, b: Rect): { d: string; head: string; cx: number; cy: number } => {
    const x1 = a.x + a.w;
    const y1 = a.y + a.h / 2;
    const x2 = b.x;
    const y2 = b.y + b.h / 2;
    const end = x2 - 8;
    let d: string;
    let cx: number;
    let cy: number;
    if (x2 - x1 >= 60) {
      const dx = (end - x1) / 2;
      d = `M${x1} ${y1} C${x1 + dx} ${y1} ${end - dx} ${y2} ${end} ${y2}`;
      cx = (x1 + x2) / 2;
      cy = (y1 + y2) / 2;
    } else {
      // Going backwards: drop between the rows and come in from the left.
      const below = b.y >= a.y + a.h;
      const above = b.y + b.h <= a.y;
      const midY = below
        ? (a.y + a.h + b.y) / 2
        : above
          ? (b.y + b.h + a.y) / 2
          : Math.max(a.y + a.h, b.y + b.h) + 36;
      const xin = Math.max(8, x2 - 40);
      d = `M${x1} ${y1} H${x1 + 20} V${midY} H${xin} V${y2} H${end}`;
      cx = (Math.min(x1 + 20, xin) + Math.max(x1 + 20, xin)) / 2;
      cy = midY;
    }
    const head = `M${end - 1} ${y2 - 5} L${x2} ${y2} L${end - 1} ${y2 + 5} Z`;
    return { d, head, cx, cy };
  };

  const edgeChip = (edge: WorkflowEdge): { label: string; set: boolean; fork: boolean } => {
    const from = graph.nodes.find((n) => n.id === edge.from);
    if (from?.kind === "fork")
      return { label: edge.branch === "no" ? "Otherwise" : "If yes", set: true, fork: true };
    return {
      label: conditionSummary(subject, edge.condition, refs.money),
      set: edge.condition !== null,
      fork: false,
    };
  };

  // ── Boxes ────────────────────────────────────────────────────────────────

  const register = (id: string) => (element: HTMLDivElement | null) => {
    if (element === null) elements.current.delete(id);
    else elements.current.set(id, element);
  };

  const tools = (node: WorkflowNode): React.JSX.Element => (
    <span className={styles.tools}>
      {(node.kind === "action" || node.kind === "wait") && (
        <>
          <button
            type="button"
            aria-label="Move earlier"
            title="Move earlier"
            disabled={swapPartner(graph, node.id, "left") === undefined}
            onClick={() => onSwap(node.id, "left")}
            data-testid="wf-node-left"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Move later"
            title="Move later"
            disabled={swapPartner(graph, node.id, "right") === undefined}
            onClick={() => onSwap(node.id, "right")}
            data-testid="wf-node-right"
          >
            ›
          </button>
        </>
      )}
      <button
        type="button"
        aria-label={`Remove box ${String(numbers.get(node.id) ?? "")}`}
        title="Remove"
        onClick={() => onRemove(node.id)}
        data-testid="wf-node-remove"
      >
        ×
      </button>
    </span>
  );

  const body = (node: WorkflowNode): React.JSX.Element => {
    const number = numbers.get(node.id);
    if (node.kind === "start")
      return (
        <>
          <div className={styles.boxHead}>
            <span className={styles.grip} aria-hidden="true">
              ⠿
            </span>
            <span className={styles.kind}>Starts</span>
          </div>
          <div className={styles.boxMain}>
            <Icon d={ICONS.start} />
            <b>{triggerSummary(subject, trigger, refs.money)}</b>
          </div>
        </>
      );
    if (node.kind === "fork")
      return (
        <>
          <div className={styles.boxHead}>
            <span className={styles.grip} aria-hidden="true">
              ⠿
            </span>
            <span className={styles.num}>{number}</span>
            <span className={styles.kind}>Fork</span>
            {tools(node)}
          </div>
          <div className={styles.boxMain}>
            <Icon d={ICONS.fork} />
            <span>
              If{" "}
              {node.condition === undefined
                ? "…"
                : conditionSummary(subject, node.condition, refs.money).replace(/^./, (c) =>
                    c.toLowerCase(),
                  )}
            </span>
          </div>
        </>
      );
    if (node.kind === "wait")
      return (
        <>
          <div className={styles.boxHead}>
            <span className={styles.grip} aria-hidden="true">
              ⠿
            </span>
            <span className={styles.num}>{number}</span>
            <span className={styles.kind}>Wait</span>
            {tools(node)}
          </div>
          <div className={styles.boxMain}>
            <Icon d={ICONS.wait} />
            <span>{waitSummary(subject, node.wait, refs.money)}</span>
          </div>
        </>
      );

    // A step.
    const on = targetSubject(graph, subject, node.target);
    const def = nodeAction(graph, subject, node);
    const person = def?.kind === "person";
    const actions = on === null ? [] : WORKFLOW_CATALOGUE[on].actions;
    const target =
      node.target === undefined || node.target === "self"
        ? null
        : (targetOptions(graph, subject, node.id).find((t) => t.value === node.target)?.label ??
          "A record that is not there");
    const who = !person
      ? "Runs on its own"
      : ring
        ? "You do it"
        : node.roleId === null || node.roleId === undefined
          ? "Someone does it — pick a role"
          : `${refs.roleName(node.roleId)} does it`;
    const title = node.params?.title;
    return (
      <>
        <div className={styles.boxHead}>
          <span className={styles.grip} aria-hidden="true">
            ⠿
          </span>
          <span className={styles.num}>{number}</span>
          <span className={person ? styles.kindPerson : styles.kindAuto}>{who}</span>
          {tools(node)}
        </div>
        <div className={styles.boxMain}>
          <Icon d={person ? ICONS.person : ICONS.auto} />
          <select
            className={styles.boxSelect}
            aria-label={`Action for step ${String(number ?? "")}`}
            value={def === null ? "" : (node.action ?? "")}
            onChange={(event) => onAction(node.id, event.target.value)}
            data-testid="wf-node-action-inline"
          >
            {def === null && <option value="">Pick an action</option>}
            <optgroup label="Runs on its own">
              {actions
                .filter((a) => a.kind === "auto")
                .map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Someone does it">
              {actions
                .filter((a) => a.kind === "person")
                .map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
            </optgroup>
          </select>
        </div>
        {(target !== null ||
          typeof title === "string" ||
          (def?.module === "TASKS" && !hasTasks)) && (
          <div className={styles.boxSmall}>
            {target !== null && <span>On: {target}</span>}
            {typeof title === "string" && title !== "" && <span>“{title}”</span>}
            {def?.module === "TASKS" && !hasTasks && (
              <span className={styles.warnText}>Needs the Tasks module</span>
            )}
          </div>
        )}
      </>
    );
  };

  const selectedNode = selection?.kind === "node" ? selection.id : null;
  const selectedEdge = selection?.kind === "edge" ? selection.id : null;

  return (
    <div className={styles.canvas} ref={view} data-testid="wf-canvas">
      <div
        className={styles.board}
        style={{ width, height }}
        onPointerDown={startPan}
        onClick={(event) => {
          if (event.target === event.currentTarget) onSelect(null);
        }}
      >
        <svg className={styles.wires} width={width} height={height} aria-hidden="true">
          {graph.edges.map((edge) => {
            const a = rects.get(edge.from);
            const b = rects.get(edge.to);
            if (a === undefined || b === undefined) return null;
            const w = wire(a, b);
            return (
              <g key={edge.id}>
                <path d={w.d} />
                <path d={w.head} className={styles.arrowHead} />
              </g>
            );
          })}
          {adds.map(({ end, rect }) => {
            const a = rects.get(end.from);
            if (a === undefined) return null;
            const w = wire(a, rect);
            return (
              <g key={`${end.from}-${end.branch ?? ""}`} className={styles.toAdd}>
                <path d={w.d} />
                <path d={w.head} className={styles.arrowHead} />
              </g>
            );
          })}
        </svg>

        {graph.edges.map((edge) => {
          const a = rects.get(edge.from);
          const b = rects.get(edge.to);
          if (a === undefined || b === undefined) return null;
          const w = wire(a, b);
          const chip = edgeChip(edge);
          const on = selectedEdge === edge.id || (chip.fork && selectedNode === edge.from);
          return (
            <button
              key={edge.id}
              type="button"
              data-chip
              className={`${styles.chip} ${chip.set ? styles.chipSet : ""} ${on ? styles.chipOn : ""}`}
              style={{ left: w.cx, top: w.cy - 8 }}
              onClick={() =>
                onSelect(
                  chip.fork ? { kind: "node", id: edge.from } : { kind: "edge", id: edge.id },
                )
              }
              aria-label={chip.fork ? chip.label : `Condition: ${chip.label}. Change it`}
              data-testid="wf-edge"
              data-edge-id={edge.id}
            >
              {chip.label}
              {!chip.fork && " ▾"}
            </button>
          );
        })}
        {adds.map(({ end, rect }) => {
          const a = rects.get(end.from);
          if (a === undefined || end.branch === undefined) return null;
          const w = wire(a, rect);
          return (
            <span
              key={`chip-${end.from}-${end.branch}`}
              data-chip
              className={`${styles.chip} ${styles.chipSet}`}
              style={{ left: w.cx, top: w.cy - 8 }}
            >
              {end.branch === "no" ? "Otherwise" : "If yes"}
            </span>
          );
        })}

        {graph.nodes.map((node) => {
          const r = rectOf(node);
          const cls = [
            styles.box,
            node.kind === "start" ? styles.boxStart : "",
            node.kind === "fork" ? styles.boxFork : "",
            node.kind === "wait" ? styles.boxWait : "",
            selectedNode === node.id ? styles.boxOn : "",
            drag?.id === node.id ? styles.dragging : "",
          ].join(" ");
          return (
            <div
              key={node.id}
              ref={register(node.id)}
              className={cls}
              style={{ left: r.x, top: r.y }}
              tabIndex={0}
              role="group"
              aria-label={
                node.kind === "start" ? "Start" : `Box ${String(numbers.get(node.id) ?? "")}`
              }
              title="Drag to move · click to edit"
              onPointerDown={(event) => startDrag(event, node)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect({ kind: "node", id: node.id });
                }
              }}
              data-testid="wf-node"
              data-node-id={node.id}
              data-kind={node.kind}
            >
              {body(node)}
            </div>
          );
        })}

        {adds.map(({ end, rect }) => (
          <div
            key={`add-${end.from}-${end.branch ?? ""}`}
            className={styles.add}
            style={{ left: rect.x, top: rect.y, width: rect.w, minHeight: rect.h }}
            data-add
            data-testid="wf-add"
            data-from={end.from}
            data-branch={end.branch ?? ""}
          >
            <button type="button" onClick={() => onAdd(end, "action")} data-testid="wf-add-step">
              <Icon d={ICONS.plus} /> Add a step
            </button>
            <span className={styles.addRow}>
              <button type="button" onClick={() => onAdd(end, "fork")} data-testid="wf-add-fork">
                Fork
              </button>
              <button type="button" onClick={() => onAdd(end, "wait")} data-testid="wf-add-wait">
                Wait
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
